import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { PageHeader, Pill, SectionCard } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth, hasPermission } from "../context/AuthContext";
import { formatInr } from "../lib/accountsSettlement";
import { fetchAllForExport } from "../lib/listPagination";
import TallySyncBadge from "../components/TallySyncBadge";
import TallySyncMappingDialog from "../components/TallySyncMappingDialog";
import {
  ArrowLeft,
  CheckSquare,
  Loader2,
  RefreshCw,
  Landmark,
} from "lucide-react";

function money(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function statusTone(status) {
  if (status === "ALLOCATED") return "success";
  if (status === "PARTIAL") return "info";
  if (status === "UNALLOCATED") return "warning";
  return "default";
}

export default function BankReconciliationPage() {
  const { user } = useAuth();
  const canEdit = hasPermission(user, "vouchers.edit") || hasPermission(user, "page.accounts");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [entries, setEntries] = useState([]);
  const [statusFilter, setStatusFilter] = useState("UNALLOCATED");
  const [selectedId, setSelectedId] = useState(null);
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [partyQuery, setPartyQuery] = useState("");
  const [bills, setBills] = useState([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [amounts, setAmounts] = useState({});
  const [onAccountAmount, setOnAccountAmount] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountFilter, setBankAccountFilter] = useState("");
  const [uploadSummary, setUploadSummary] = useState(null);
  const [fallbackAccountId, setFallbackAccountId] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [acctForm, setAcctForm] = useState({
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    tally_ledger_name: "",
  });
  const [savingAccount, setSavingAccount] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [mobileView, setMobileView] = useState("entries");
  const [tallySyncTargets, setTallySyncTargets] = useState(null);
  const canTallySync = hasPermission(user, "tally.sync") || canEdit;

  const openTallyMapping = (voucherIds) => {
    const targets = (voucherIds || [])
      .map((row) => (typeof row === "number" ? { id: row } : { id: row?.id, code: row?.code || row?.cash_voucher_code }))
      .filter((row) => row.id);
    if (!targets.length) return;
    setTallySyncTargets(targets);
  };

  const selected = useMemo(
    () => entries.find((e) => e.id === selectedId) || null,
    [entries, selectedId]
  );

  const reloadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/accounts/bank-accounts");
      setBankAccounts(data?.accounts || []);
    } catch (err) {
      toast.error(extractError(err));
    }
  }, []);

  const reloadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (statusFilter) params.status = statusFilter;
      if (bankAccountFilter) params.bank_account_id = bankAccountFilter;
      const { data } = await api.get("/accounts/bank-statements/entries", { params });
      const rows = data?.entries || [];
      setEntries(rows);
      setSelectedId((prev) => {
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return rows[0]?.id ?? null;
      });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, bankAccountFilter]);

  useEffect(() => {
    reloadEntries();
  }, [reloadEntries]);

  useEffect(() => {
    reloadAccounts();
  }, [reloadAccounts]);

  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchAllForExport(api, "/parties", { ordering: "display_name" });
        setParties(rows);
      } catch (err) {
        toast.error(extractError(err));
      }
    })();
  }, []);

  useEffect(() => {
    setPartyId("");
    setAmounts({});
    setBills([]);
    setOnAccountAmount("");
    setAdvanceAmount("");
  }, [selectedId]);

  useEffect(() => {
    if (!selected || !partyId) {
      setBills([]);
      setAmounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      setBillsLoading(true);
      try {
        const { data } = await api.get(`/accounts/parties/${partyId}/open-bills`, {
          params: { entry_id: selected.id },
        });
        if (cancelled) return;
        setBills(data?.bills || []);
        setAmounts({});
      } catch (err) {
        if (!cancelled) toast.error(extractError(err));
      } finally {
        if (!cancelled) setBillsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, partyId]);

  const filteredParties = useMemo(() => {
    const q = partyQuery.trim().toLowerCase();
    if (!q) return parties.slice(0, 80);
    return parties
      .filter((p) => {
        const name = (p.display_name || p.name || "").toLowerCase();
        const code = String(p.code || p.id || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      })
      .slice(0, 80);
  }, [parties, partyQuery]);

  const txnAmount = money(selected?.unallocated_amount ?? selected?.amount ?? 0);
  const billAllocated = useMemo(
    () => money(Object.values(amounts).reduce((s, v) => s + money(v), 0)),
    [amounts]
  );
  const onAccountAllocated = money(onAccountAmount || 0);
  const advanceAllocated = money(advanceAmount || 0);
  const totalAllocated = money(billAllocated + onAccountAllocated + advanceAllocated);
  const remaining = money(txnAmount - totalAllocated);

  const uploadFile = async (file, accountId) => {
    if (!file || !canEdit) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const aid = accountId || fallbackAccountId;
      if (aid) fd.append("bank_account_id", String(aid));
      const { data } = await api.post("/accounts/bank-statements/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadSummary(data);
      setPendingFile(null);
      const imported = data?.new_imported ?? data?.total_entries ?? 0;
      const skipped = data?.duplicates_skipped ?? 0;
      toast.success(`Imported ${imported} new row(s)` + (skipped ? ` · skipped ${skipped} duplicate(s)` : ""));
      setStatusFilter("UNALLOCATED");
      if (data?.bank_account_id) setBankAccountFilter(String(data.bank_account_id));
      await reloadEntries();
      if (data?.entries?.[0]?.id) setSelectedId(data.entries[0].id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "BANK_ACCOUNT_REQUIRED") {
        setPendingFile(file);
        if (detail.bank_accounts) setBankAccounts(detail.bank_accounts);
        toast.error(detail.message || "Select which bank account this statement belongs to");
      } else {
        toast.error(extractError(err));
      }
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const setAmount = (voucherId, value) => {
    const n = value === "" ? "" : money(value);
    setAmounts((prev) => {
      const next = { ...prev };
      if (n === "" || n <= 0) delete next[voucherId];
      else next[voucherId] = n;
      return next;
    });
  };

  const toggleBill = (bill) => {
    const id = bill.voucher_id;
    if (amounts[id] != null) {
      setAmount(id, "");
      return;
    }
    const alreadyOnBill = money(amounts[id] || 0);
    const room = money(txnAmount - totalAllocated + alreadyOnBill);
    if (room <= 0) {
      toast.message("Transaction fully assigned — clear a line or reduce on account / advance");
      return;
    }
    const fill = money(Math.min(room, bill.remaining_balance));
    setAmount(id, fill);
  };

  const fifoFill = () => {
    if (!selected || !bills.length) return;
    let left = money(txnAmount - onAccountAllocated - advanceAllocated);
    const next = {};
    for (const bill of bills) {
      if (left <= 0) break;
      const take = money(Math.min(left, bill.remaining_balance));
      if (take > 0) {
        next[bill.voucher_id] = take;
        left = money(left - take);
      }
    }
    setAmounts(next);
  };

  const putRemainderTo = (kind) => {
    const leftover = money(txnAmount - billAllocated - (kind === "ADVANCE" ? onAccountAllocated : advanceAllocated));
    if (leftover <= 0) {
      toast.message("Nothing left to assign — reduce bill amounts first");
      return;
    }
    if (kind === "ADVANCE") setAdvanceAmount(String(leftover));
    else setOnAccountAmount(String(leftover));
  };

  const resyncVoucher = async (voucherId, voucherCode) => {
    if (!voucherId || !canTallySync) return;
    // Always open mapping dialog first — it loads pending ledgers (e.g. bank → HDFC) then syncs.
    openTallyMapping([{ id: voucherId, code: voucherCode || `#${voucherId}` }]);
  };

  const postAllocation = async () => {
    if (!selected || !partyId || !canEdit) return;
    if (totalAllocated <= 0) {
      toast.error("Allocate against bills and/or enter on account / advance amounts");
      return;
    }
    if (totalAllocated > txnAmount + 0.009) {
      toast.error("Allocated amount exceeds bank transaction");
      return;
    }

    const payload = {
      entry_id: selected.id,
      party_id: Number(partyId),
      allocations: Object.entries(amounts)
        .map(([voucher_id, allocated_amount]) => ({
          voucher_id: Number(voucher_id),
          allocated_amount: money(allocated_amount),
          allocation_mode: "VOUCHER",
        }))
        .filter((l) => l.allocated_amount > 0),
    };
    if (onAccountAllocated > 0) payload.on_account_amount = onAccountAllocated;
    if (advanceAllocated > 0) payload.advance_amount = advanceAllocated;

    setPosting(true);
    try {
      const { data } = await api.post("/accounts/bank-statements/allocate", payload);
      const tally = data?.tally_sync;
      const cashIds = (data?.cash_voucher_ids || []).map((id, i) => ({
        id,
        code: tally?.vouchers?.[i]?.code || data?.allocations?.[i]?.cash_voucher_code || `#${id}`,
      }));
      const count = (data?.allocations || []).length || 1;
      await reloadEntries();
      setAmounts({});
      setOnAccountAmount("");
      setAdvanceAmount("");
      setBills([]);
      setPartyId("");

      if (tally?.needs_mapping || (cashIds.length && !(tally?.queued > 0))) {
        toast.message("Posted as one bank voucher — map pending Tally ledgers to sync");
        openTallyMapping(cashIds.length ? cashIds : (tally?.vouchers || []));
      } else if (tally?.queued) {
        toast.success(
          `Posted 1 bank voucher (${count} allocation line(s)) · Tally queued ${tally.queued}` +
            (tally.delivered ? ` · delivered ${tally.delivered}` : "")
        );
      } else {
        toast.success(`Posted 1 bank voucher (${count} allocation line(s))`);
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bank-reconciliation-page">
      <PageHeader
        title="Bank reconciliation"
        description="Upload bank statements. New rows are imported; duplicates from earlier files are skipped. Payments/receipts post to the mapped Tally bank ledger."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/accounts">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Accounts
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={reloadEntries} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />

      <SectionCard
        title="Statement upload"
        subtitle="Excel (.xlsx) or CSV. Account number is read from the header when present."
      >
        <label
          className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-bansal-sage bg-bansal-sage/15"
              : "border-border bg-muted/40 hover:border-bansal-sage/60"
          } ${!canEdit || uploading ? "opacity-60 pointer-events-none" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <Landmark className="h-8 w-8 text-primary" />
          <div className="text-sm font-medium text-foreground">
            {uploading ? "Parsing statement…" : "Drop Excel/CSV here, or click to browse"}
          </div>
          <div className="text-xs text-muted-foreground">
            HDFC, ICICI, SBI, Axis and similar Date / Deposit / Withdrawal layouts
          </div>
          <input
            type="file"
            accept=".xlsx,.xls,.csv,.txt"
            className="hidden"
            disabled={!canEdit || uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadFile(f);
              e.target.value = "";
            }}
          />
          {uploading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
        </label>
        {pendingFile ? (
          <div className="mt-3 rounded-md border border-border bg-card px-3 py-3 space-y-2">
            <p className="text-sm text-foreground">Account number not found in the file. Choose the bank account, then import.</p>
            <div className="flex flex-wrap gap-2">
              <select
                className="h-9 rounded-md border border-border bg-background px-2 text-sm min-w-[16rem]"
                value={fallbackAccountId}
                onChange={(e) => setFallbackAccountId(e.target.value)}
              >
                <option value="">Select bank account…</option>
                {bankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.bank_name} · {a.account_number}
                    {a.tally_ledger_name ? ` → ${a.tally_ledger_name}` : " (map Tally ledger)"}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                disabled={!fallbackAccountId || uploading}
                onClick={() => uploadFile(pendingFile, fallbackAccountId)}
              >
                Import with this account
              </Button>
            </div>
          </div>
        ) : null}
        {uploadSummary ? (
          <div className="mt-3 rounded-md border border-border bg-card px-3 py-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Detected account</div>
              <div className="font-medium text-foreground">
                {(uploadSummary.bank_name || "Bank") +
                  (uploadSummary.account_number ? ` · A/C ${uploadSummary.account_number}` : "")}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Mapped Tally ledger</div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">
                  {uploadSummary.tally_ledger || "Not mapped"}
                </span>
                <Pill tone={uploadSummary.mapping_status === "MAPPED" ? "success" : "warning"}>
                  {uploadSummary.mapping_status === "MAPPED" ? "MAPPED" : "MAPPING REQUIRED"}
                </Pill>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Statement summary</div>
              <div className="text-foreground">
                {uploadSummary.total_rows_in_file ?? 0} total in file · {uploadSummary.new_imported ?? 0} new ·{" "}
                {uploadSummary.duplicates_skipped ?? 0} duplicates ignored
              </div>
            </div>
          </div>
        ) : null}
      </SectionCard>

      <SectionCard title="Bank accounts" subtitle="Map each A/c number to the exact Tally bank ledger name.">
        <div className="flex flex-wrap gap-2 mb-3">
          <Input
            className="max-w-[8rem]"
            placeholder="HDFC"
            value={acctForm.bank_name}
            onChange={(e) => setAcctForm((f) => ({ ...f, bank_name: e.target.value }))}
          />
          <Input
            className="max-w-[12rem]"
            placeholder="Account number"
            value={acctForm.account_number}
            onChange={(e) => setAcctForm((f) => ({ ...f, account_number: e.target.value }))}
          />
          <Input
            className="max-w-[8rem]"
            placeholder="IFSC"
            value={acctForm.ifsc_code}
            onChange={(e) => setAcctForm((f) => ({ ...f, ifsc_code: e.target.value }))}
          />
          <Input
            className="min-w-[12rem] flex-1"
            placeholder="Tally ledger name"
            value={acctForm.tally_ledger_name}
            onChange={(e) => setAcctForm((f) => ({ ...f, tally_ledger_name: e.target.value }))}
          />
          <Button
            type="button"
            size="sm"
            disabled={!canEdit || savingAccount}
            onClick={async () => {
              if (!acctForm.account_number.trim()) {
                toast.error("Account number is required");
                return;
              }
              setSavingAccount(true);
              try {
                await api.post("/accounts/bank-accounts", acctForm);
                toast.success("Bank account saved");
                setAcctForm({ bank_name: "", account_number: "", ifsc_code: "", tally_ledger_name: "" });
                await reloadAccounts();
              } catch (err) {
                toast.error(extractError(err));
              } finally {
                setSavingAccount(false);
              }
            }}
          >
            Save account
          </Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {bankAccounts.map((a) => (
            <Pill key={a.id} tone={a.mapping_status === "MAPPED" ? "success" : "warning"}>
              {a.bank_name} · {a.account_number}
              {a.tally_ledger_name ? ` → ${a.tally_ledger_name}` : ""}
            </Pill>
          ))}
          {!bankAccounts.length ? (
            <span className="text-xs text-muted-foreground">No bank accounts registered yet.</span>
          ) : null}
        </div>
      </SectionCard>

      {/* Mobile switcher: Bank entries | Bill allocator */}
      <div className="xl:hidden grid grid-cols-2 gap-1 rounded-xl border border-border bg-muted/40 p-1" role="tablist" data-testid="bankrec-mobile-switcher">
        <button
          type="button"
          role="tab"
          aria-selected={mobileView === "entries"}
          onClick={() => setMobileView("entries")}
          data-testid="bankrec-tab-entries"
          className={`min-h-11 rounded-lg text-sm font-medium transition-colors ${
            mobileView === "entries" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
          }`}
        >
          Bank entries{entries.length ? ` (${entries.length})` : ""}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobileView === "allocator"}
          onClick={() => setMobileView("allocator")}
          data-testid="bankrec-tab-allocator"
          className={`min-h-11 rounded-lg text-sm font-medium transition-colors ${
            mobileView === "allocator" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
          }`}
        >
          Bill allocator
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 min-h-[28rem]">
        <SectionCard
          className={`xl:col-span-2 ${mobileView === "entries" ? "" : "hidden xl:block"}`}
          title="Statement lines"
          subtitle="Select a row to allocate."
          action={
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="h-8 rounded-md border border-border bg-background px-2 text-xs"
                value={bankAccountFilter}
                onChange={(e) => setBankAccountFilter(e.target.value)}
              >
                <option value="">All accounts</option>
                {bankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.bank_name} · {a.account_number}
                  </option>
                ))}
              </select>
              <select
                className="h-8 rounded-md border border-border bg-background px-2 text-xs"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="UNALLOCATED">Open (unallocated / partial)</option>
                <option value="PARTIAL">Partial only</option>
                <option value="ALLOCATED">Fully allocated</option>
                <option value="">All</option>
              </select>
            </div>
          }
        >
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : entries.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No statement rows yet.</p>
          ) : (
            <ul className="divide-y divide-border max-h-[32rem] overflow-y-auto -mx-1">
              {entries.map((row) => {
                const active = row.id === selectedId;
                const cr = row.direction === "CR";
                const acct = bankAccounts.find((a) => String(a.id) === String(row.bank_account_id));
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(row.id);
                        setMobileView("allocator");
                      }}
                      className={`w-full text-left px-3 py-2.5 transition-colors ${
                        active ? "bg-bansal-sage/25" : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs text-muted-foreground">{row.txn_date}</div>
                          {acct ? (
                            <div className="text-[11px] text-muted-foreground truncate">
                              {acct.bank_name} · {acct.account_number}
                            </div>
                          ) : null}
                          <div className="text-sm text-foreground truncate" title={row.narration}>
                            {row.narration || "—"}
                          </div>
                          {row.ref_number ? (
                            <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
                              {row.ref_number}
                            </div>
                          ) : null}
                        </div>
                        <div className="shrink-0 text-right space-y-1">
                          <span
                            className={`inline-flex text-xs font-semibold px-1.5 py-0.5 rounded border border-border ${
                              cr ? "bg-muted text-foreground" : "bg-card text-foreground"
                            }`}
                          >
                            {cr ? "Cr" : "Dr"} {formatInr(row.amount)}
                          </span>
                          <div>
                            <Pill tone={statusTone(row.status)}>{row.status}</Pill>
                          </div>
                          {row.tally_sync_status ? (
                            <div className="flex justify-end">
                              <TallySyncBadge
                                status={row.tally_sync_status}
                                error={row.tally_error_message}
                                showDot={false}
                              />
                            </div>
                          ) : null}
                          {row.status !== "UNALLOCATED" ? (
                            <div className="text-[11px] text-muted-foreground">
                              Left {formatInr(row.unallocated_amount)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          className={`xl:col-span-3 ${mobileView === "allocator" ? "" : "hidden xl:block"}`}
          title="Allocation"
          subtitle={
            selected
              ? `${selected.direction === "CR" ? "Bank receipt" : "Bank payment"} · unallocated ${formatInr(txnAmount)}`
              : "Select a bank line on the left."
          }
        >
          {!selected ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No row selected.</p>
          ) : (
            <div className="space-y-4">
              {(selected.allocations || []).length > 0 ? (
                <div className="rounded-md border border-border bg-muted/40 px-3 py-2 space-y-2">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Posted voucher · bill allocations · Tally
                  </div>
                  <ul className="space-y-3">
                    {Object.values(
                      (selected.allocations || []).reduce((acc, alloc) => {
                        const key = String(alloc.cash_voucher_id || alloc.id || Math.random());
                        if (!acc[key]) {
                          acc[key] = {
                            cash_voucher_id: alloc.cash_voucher_id,
                            cash_voucher_code: alloc.cash_voucher_code,
                            tally_sync_status: alloc.tally_sync_status,
                            tally_error_message: alloc.tally_error_message,
                            lines: [],
                          };
                        }
                        acc[key].lines.push(alloc);
                        // Prefer failed status if any line has it
                        if (alloc.tally_sync_status === "FAILED") {
                          acc[key].tally_sync_status = "FAILED";
                          acc[key].tally_error_message = alloc.tally_error_message;
                        }
                        return acc;
                      }, {})
                    ).map((group) => (
                      <li key={group.cash_voucher_id || group.cash_voucher_code} className="space-y-1.5">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                          <span className="font-mono text-xs font-medium">
                            {group.cash_voucher_code || "—"}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            <TallySyncBadge
                              status={group.tally_sync_status}
                              error={group.tally_error_message}
                              showDot={false}
                            />
                            {canTallySync &&
                            group.cash_voucher_id &&
                            ["FAILED", "RE_SYNC_REQUIRED", "NOT_SYNCED", "PENDING"].includes(
                              group.tally_sync_status
                            ) ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() =>
                                  resyncVoucher(group.cash_voucher_id, group.cash_voucher_code)
                                }
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                {group.tally_sync_status === "FAILED"
                                  ? "Map & sync to Tally"
                                  : "Re-sync to Tally"}
                              </Button>
                            ) : null}
                          </div>
                        </div>
                        <ul className="pl-2 border-l border-border space-y-0.5">
                          {group.lines.map((alloc) => (
                            <li
                              key={alloc.id || `${alloc.target_voucher_code}-${alloc.allocated_amount}`}
                              className="text-xs text-muted-foreground"
                            >
                              → {alloc.target_voucher_code ||
                                (alloc.allocation_mode === "ADVANCE"
                                  ? "Advance"
                                  : alloc.allocation_mode === "ON_ACCOUNT"
                                    ? "On Account"
                                    : "—")}{" "}
                              · {formatInr(alloc.allocated_amount)}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Party search</label>
                  <Input
                    value={partyQuery}
                    onChange={(e) => setPartyQuery(e.target.value)}
                    placeholder="Type party name…"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Party</label>
                  <select
                    className="mt-1 w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                    value={partyId}
                    onChange={(e) => setPartyId(e.target.value)}
                    disabled={!canEdit}
                  >
                    <option value="">Select party…</option>
                    {filteredParties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.display_name || p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={fifoFill}
                  disabled={!partyId || !bills.length || !canEdit}
                >
                  <CheckSquare className="h-4 w-4 mr-1" />
                  FIFO fill bills
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setAmounts({})}
                  disabled={!Object.keys(amounts).length}
                >
                  Clear bills
                </Button>
              </div>

              {billsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading open bills…
                </div>
              ) : !partyId ? (
                <p className="text-sm text-muted-foreground">Choose a party to load open bills.</p>
              ) : bills.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No open bills — you can still post the amount as on account and/or advance below.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium px-3 py-2 w-10" />
                        <th className="text-left font-medium px-3 py-2">Invoice</th>
                        <th className="text-left font-medium px-3 py-2">Date</th>
                        <th className="text-right font-medium px-3 py-2">Total</th>
                        <th className="text-right font-medium px-3 py-2">Balance</th>
                        <th className="text-right font-medium px-3 py-2">Allocate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bills.map((bill) => {
                        const checked = amounts[bill.voucher_id] != null;
                        return (
                          <tr key={bill.voucher_id} className="border-t border-border">
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleBill(bill)}
                                disabled={!canEdit}
                              />
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {bill.invoice_no}
                              <div className="text-[10px] text-muted-foreground">{bill.voucher_type}</div>
                            </td>
                            <td className="px-3 py-2 text-xs">{bill.date || "—"}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatInr(bill.total_amount)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                              {formatInr(bill.remaining_balance)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                className="h-8 w-28 ml-auto text-right"
                                value={amounts[bill.voucher_id] ?? ""}
                                disabled={!canEdit}
                                onChange={(e) => setAmount(bill.voucher_id, e.target.value)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="rounded-md border border-border bg-muted/30 px-3 py-3 space-y-3">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Split remainder (same transaction)
                </div>
                <p className="text-xs text-muted-foreground">
                  Example: ₹6,00,000 with ₹4,00,000 against bills and ₹2,00,000 as advance — fill both in one post.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">On account</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="mt-1 h-10 text-right tabular-nums"
                      value={onAccountAmount}
                      disabled={!canEdit || !partyId}
                      placeholder="0"
                      onChange={(e) => setOnAccountAmount(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-1 h-7 px-2"
                      disabled={!canEdit || !partyId || remaining <= 0}
                      onClick={() => putRemainderTo("ON_ACCOUNT")}
                    >
                      Put remaining here
                    </Button>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Advance</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="mt-1 h-10 text-right tabular-nums"
                      value={advanceAmount}
                      disabled={!canEdit || !partyId}
                      placeholder="0"
                      onChange={(e) => setAdvanceAmount(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-1 h-7 px-2"
                      disabled={!canEdit || !partyId || remaining <= 0}
                      onClick={() => putRemainderTo("ADVANCE")}
                    >
                      Put remaining here
                    </Button>
                  </div>
                </div>
                {(billAllocated > 0 || onAccountAllocated > 0 || advanceAllocated > 0) && (
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                    <span>Bills {formatInr(billAllocated)}</span>
                    <span>On account {formatInr(onAccountAllocated)}</span>
                    <span>Advance {formatInr(advanceAllocated)}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-end justify-between gap-3 rounded-md border border-border bg-muted/50 px-3 py-3 sticky bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-20 backdrop-blur shadow-[0_-8px_20px_-10px_rgba(0,0,0,0.28)] lg:static lg:bottom-auto lg:z-auto lg:backdrop-blur-0 lg:shadow-none" data-testid="bankrec-summary-bar">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Transaction</div>
                    <div className="font-semibold tabular-nums">{formatInr(txnAmount)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Total allocated</div>
                    <div className="font-semibold tabular-nums">{formatInr(totalAllocated)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Remaining</div>
                    <div
                      className={`font-semibold tabular-nums ${
                        remaining < 0 ? "text-red-700" : "text-foreground"
                      }`}
                    >
                      {formatInr(remaining)}
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={postAllocation}
                  disabled={
                    !canEdit ||
                    !partyId ||
                    posting ||
                    totalAllocated <= 0 ||
                    remaining < -0.009
                  }
                >
                  {posting ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Landmark className="h-4 w-4 mr-1" />
                  )}
                  Post allocation
                </Button>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      <TallySyncMappingDialog
        open={Boolean(tallySyncTargets?.length)}
        onOpenChange={(open) => {
          if (!open) setTallySyncTargets(null);
        }}
        voucherIds={tallySyncTargets || []}
        onSynced={async () => {
          setTallySyncTargets(null);
          await reloadEntries();
        }}
      />
    </div>
  );
}
